import {
  type EConnectorType,
  ECrowdstrikeRegion,
  EIntegrationProvider,
  EMcpAuthMode,
  EMcpTransport,
  type TAtlassianIntegrationProvider,
  type TAzureIntegrationProvider,
  type TDropboxIntegrationProvider,
  type TGoogleIntegrationProvider,
} from "../types/integration";

export const INTEGRATION_QUERY_KEY = "integrations";

export const ASSISTANTS_QUERY_KEY = "assistants";

export const INTEGRATION_CONFIGS_QUERY_KEY = "integration-configs";

export const ENABLED_INTEGRATIONS_QUERY_KEY = "enabled-integrations";

export const CROWDSTRIKE_REGION_OPTIONS = [
  { label: "US1 (https://api.crowdstrike.com)", value: ECrowdstrikeRegion.US1 },
  {
    label: "US2 (https://api.us-2.crowdstrike.com)",
    value: ECrowdstrikeRegion.US2,
  },
  {
    label: "EU1 (https://api.eu-1.crowdstrike.com)",
    value: ECrowdstrikeRegion.EU1,
  },
];

export const DIRECT_INTEGRATION_CONNECTION_PROVIDERS = [
  EIntegrationProvider.AZURE_MES,
  EIntegrationProvider.FIREFLIES,
  EIntegrationProvider.CROWDSTRIKE,
  EIntegrationProvider.KONTUR_FOCUS,
  EIntegrationProvider.MCPS,
  // NOTE: The credential flow completes server-side, so the response URL is a
  // result to parse rather than somewhere to navigate.
  EIntegrationProvider.NEXTCLOUD,
];

// NOTE: Atlassian Data Center connectors are configured per-organization, so
// they can only be connected inside an organization project. Web and mobile
// hide these cards in personal projects; the server also rejects the connect.
export const ATLASSIAN_DC_PROVIDERS = [
  EIntegrationProvider.ATLASSIAN_JIRA_DC,
  EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
];

export const BAMBOOHR_COMPANY_DOMAIN_REGEX = /^[a-zA-Z0-9-]+$/;

// NOTE: NSAI-4328 — E.164 with no separators, which is the only shape
// Telegram's login accepts, and the five-digit code its service sends.
export const TELEGRAM_PHONE_NUMBER_REGEX = /^\+?[1-9]\d{1,14}$/;
export const TELEGRAM_CODE_REGEX = /^\d{5}$/;

// NOTE: Login Flow v2 status polling. Two seconds is responsive enough that the
// user sees the connection land right after authorising, without hammering the
// endpoint for the token's full 20-minute lifetime.
export const NEXTCLOUD_LOGIN_FLOW_POLL_INTERVAL_MS = 2000;

// NOTE: NSAI-4328 — this and the interval above lived in
// `web/src/constants/integrations.ts` until mobile needed the same flow.
// Nothing about either is platform-specific. The 410-Gone status that flow also
// checks was already in `constants/app.ts` — use that one.
export const NEXTCLOUD_LOGIN_FLOW_QUERY_KEY = "nextcloud-login-flow";

export type TIntegrationModalKey =
  | "mes"
  | "fireflies"
  | "crowdstrike"
  | "konturFocus"
  | "konturDiadoc"
  | "mcp"
  | "telegram"
  | "bamboohr"
  | "nextcloud";

export interface IIntegrationDefinition {
  id: EIntegrationProvider;
  nameKey: string;
  descriptionKey: string;
  provider: EIntegrationProvider | null;
  integrationType?:
    | TAtlassianIntegrationProvider
    | TGoogleIntegrationProvider
    | TAzureIntegrationProvider
    | TDropboxIntegrationProvider;
  modalKey?: TIntegrationModalKey;
  fallbackToExternalProvider?: boolean;
  skipExternalProviderCheck?: boolean;
  // NOTE: List of connectors the user can pick from when initiating a connect
  // flow. When length > 1 the UI must surface a SelectConnectorModal so the
  // user explicitly picks an implementation; when length === 1 (or omitted) the
  // single available connector is used and no modal is shown.
  availableConnectors?: EConnectorType[];
}

export const COMPOSIO_INTEGRATION_DEFINITIONS: IIntegrationDefinition[] = [
  {
    id: EIntegrationProvider.AIRTABLE,
    nameKey: "list.airtable",
    descriptionKey: "list.airtableDescription",
    provider: EIntegrationProvider.AIRTABLE,
  },
  {
    id: EIntegrationProvider.CALENDLY,
    nameKey: "list.calendly",
    descriptionKey: "list.calendlyDescription",
    provider: EIntegrationProvider.CALENDLY,
  },
  {
    id: EIntegrationProvider.ATLASSIAN_CONFLUENCE,
    nameKey: "list.atlassianConfluence",
    descriptionKey: "list.atlassianConfluenceDescription",
    provider: EIntegrationProvider.ATLASSIAN_CONFLUENCE,
    integrationType: EIntegrationProvider.ATLASSIAN_CONFLUENCE,
  },
  {
    id: EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
    nameKey: "list.atlassianConfluenceDc",
    descriptionKey: "list.atlassianConfluenceDcDescription",
    provider: EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
    integrationType: EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
  },
  {
    id: EIntegrationProvider.ATLASSIAN_JIRA,
    nameKey: "list.atlassianJira",
    descriptionKey: "list.atlassianJiraDescription",
    provider: EIntegrationProvider.ATLASSIAN_JIRA,
    integrationType: EIntegrationProvider.ATLASSIAN_JIRA,
  },
  {
    id: EIntegrationProvider.ATLASSIAN_JIRA_DC,
    nameKey: "list.atlassianJiraDc",
    descriptionKey: "list.atlassianJiraDcDescription",
    provider: EIntegrationProvider.ATLASSIAN_JIRA_DC,
    integrationType: EIntegrationProvider.ATLASSIAN_JIRA_DC,
  },
  {
    id: EIntegrationProvider.AZURE_OUTLOOK,
    nameKey: "list.azureOutlook",
    descriptionKey: "list.azureOutlookDescription",
    provider: EIntegrationProvider.AZURE_OUTLOOK,
    integrationType: EIntegrationProvider.AZURE_OUTLOOK,
  },
  {
    id: EIntegrationProvider.AZURE_CALENDAR,
    nameKey: "list.azureCalendar",
    descriptionKey: "list.azureCalendarDescription",
    provider: EIntegrationProvider.AZURE_CALENDAR,
    integrationType: EIntegrationProvider.AZURE_CALENDAR,
  },
  {
    id: EIntegrationProvider.AZURE_ONEDRIVE,
    nameKey: "list.azureOneDrive",
    descriptionKey: "list.azureOneDriveDescription",
    provider: EIntegrationProvider.AZURE_ONEDRIVE,
    integrationType: EIntegrationProvider.AZURE_ONEDRIVE,
  },
  {
    id: EIntegrationProvider.AZURE_SHARE_POINT,
    nameKey: "list.azureSharePoint",
    descriptionKey: "list.azureSharePointDescription",
    provider: EIntegrationProvider.AZURE_SHARE_POINT,
    integrationType: EIntegrationProvider.AZURE_SHARE_POINT,
  },
  {
    id: EIntegrationProvider.AZURE_EXCEL,
    nameKey: "list.azureExcel",
    descriptionKey: "list.azureExcelDescription",
    provider: EIntegrationProvider.AZURE_EXCEL,
    integrationType: EIntegrationProvider.AZURE_EXCEL,
  },
  {
    id: EIntegrationProvider.AZURE_MICROSOFT_TEAMS,
    nameKey: "list.azureMicrosoftTeams",
    descriptionKey: "list.azureMicrosoftTeamsDescription",
    provider: EIntegrationProvider.AZURE_MICROSOFT_TEAMS,
    integrationType: EIntegrationProvider.AZURE_MICROSOFT_TEAMS,
  },
  {
    id: EIntegrationProvider.AZURE_MES,
    nameKey: "list.azureMES",
    descriptionKey: "list.azureMESDescription",
    provider: EIntegrationProvider.AZURE_MES,
    modalKey: "mes",
  },
  {
    id: EIntegrationProvider.GITHUB,
    nameKey: "list.github",
    descriptionKey: "list.githubDescription",
    provider: EIntegrationProvider.GITHUB,
  },
  {
    id: EIntegrationProvider.GITLAB,
    nameKey: "list.gitlab",
    descriptionKey: "list.gitlabDescription",
    provider: EIntegrationProvider.GITLAB,
  },
  {
    id: EIntegrationProvider.GOOGLE_DRIVE,
    nameKey: "list.googleDrive",
    descriptionKey: "list.googleDriveDescription",
    provider: EIntegrationProvider.GOOGLE_DRIVE,
    integrationType: EIntegrationProvider.GOOGLE_DRIVE,
  },
  {
    id: EIntegrationProvider.GOOGLE_SHEETS,
    nameKey: "list.googleSheets",
    descriptionKey: "list.googleSheetsDescription",
    provider: EIntegrationProvider.GOOGLE_SHEETS,
    integrationType: EIntegrationProvider.GOOGLE_SHEETS,
  },
  {
    id: EIntegrationProvider.GOOGLE_CALENDAR,
    nameKey: "list.googleCalendar",
    descriptionKey: "list.googleCalendarDescription",
    provider: EIntegrationProvider.GOOGLE_CALENDAR,
    integrationType: EIntegrationProvider.GOOGLE_CALENDAR,
  },
  {
    id: EIntegrationProvider.GOOGLE_MEET,
    nameKey: "list.googleMeet",
    descriptionKey: "list.googleMeetDescription",
    provider: EIntegrationProvider.GOOGLE_MEET,
    integrationType: EIntegrationProvider.GOOGLE_MEET,
  },
  {
    id: EIntegrationProvider.GOOGLE_MAIL,
    nameKey: "list.googleMail",
    descriptionKey: "list.googleMailDescription",
    provider: EIntegrationProvider.GOOGLE_MAIL,
    integrationType: EIntegrationProvider.GOOGLE_MAIL,
  },
  {
    id: EIntegrationProvider.HUBSPOT,
    nameKey: "list.hubspot",
    descriptionKey: "list.hubspotDescription",
    provider: EIntegrationProvider.HUBSPOT,
  },
  {
    id: EIntegrationProvider.NOTION,
    nameKey: "list.notion",
    descriptionKey: "list.notionDescription",
    provider: EIntegrationProvider.NOTION,
  },
  {
    id: EIntegrationProvider.LINEAR,
    nameKey: "list.linear",
    descriptionKey: "list.linearDescription",
    provider: EIntegrationProvider.LINEAR,
  },
  {
    id: EIntegrationProvider.ZENDESK,
    nameKey: "list.zendesk",
    descriptionKey: "list.zendeskDescription",
    provider: EIntegrationProvider.ZENDESK,
  },
  {
    id: EIntegrationProvider.SMARTSHEET,
    nameKey: "list.smartsheet",
    descriptionKey: "list.smartsheetDescription",
    provider: EIntegrationProvider.SMARTSHEET,
  },
  {
    id: EIntegrationProvider.DATABRICKS,
    nameKey: "list.databricks",
    descriptionKey: "list.databricksDescription",
    provider: EIntegrationProvider.DATABRICKS,
  },
  {
    id: EIntegrationProvider.ZOHO_CRM,
    nameKey: "list.zohoCrm",
    descriptionKey: "list.zohoCrmDescription",
    provider: EIntegrationProvider.ZOHO_CRM,
  },
  {
    id: EIntegrationProvider.SLACK,
    nameKey: "list.slack",
    descriptionKey: "list.slackDescription",
    provider: EIntegrationProvider.SLACK,
  },
  {
    id: EIntegrationProvider.TODOIST,
    nameKey: "list.todoist",
    descriptionKey: "list.todoistDescription",
    provider: EIntegrationProvider.TODOIST,
  },
  {
    id: EIntegrationProvider.ASANA,
    nameKey: "list.asana",
    descriptionKey: "list.asanaDescription",
    provider: EIntegrationProvider.ASANA,
  },
  {
    id: EIntegrationProvider.BITRIX24,
    nameKey: "list.bitrix24",
    descriptionKey: "list.bitrix24Description",
    provider: EIntegrationProvider.BITRIX24,
    skipExternalProviderCheck: true,
  },
  {
    id: EIntegrationProvider.FIREFLIES,
    nameKey: "list.fireflies",
    descriptionKey: "list.firefliesDescription",
    provider: EIntegrationProvider.FIREFLIES,
    modalKey: "fireflies",
    // NOTE: NSAI-4170 — Fireflies is native-only now, so it must open its
    // API-key config modal instead of auto-routing to the external (Composio)
    // provider. Without this, an enabled Composio key (isExternalIntegrationsProviderEnabled)
    // would bypass the modal and hit the native connect URL with no API key.
  },
  {
    id: EIntegrationProvider.CROWDSTRIKE,
    nameKey: "list.crowdstrike",
    descriptionKey: "list.crowdstrikeDescription",
    provider: EIntegrationProvider.CROWDSTRIKE,
    modalKey: "crowdstrike",
  },
  {
    id: EIntegrationProvider.KONTUR_DIADOC,
    nameKey: "list.konturDiadoc",
    descriptionKey: "list.konturDiadocDescription",
    provider: EIntegrationProvider.KONTUR_DIADOC,
    modalKey: "konturDiadoc",
    fallbackToExternalProvider: true,
  },
  {
    id: EIntegrationProvider.KONTUR_FOCUS,
    nameKey: "list.konturFocus",
    descriptionKey: "list.konturFocusDescription",
    provider: EIntegrationProvider.KONTUR_FOCUS,
    modalKey: "konturFocus",
    fallbackToExternalProvider: true,
  },
  {
    id: EIntegrationProvider.MCPS,
    nameKey: "list.mcps",
    descriptionKey: "list.mcpsDescription",
    provider: EIntegrationProvider.MCPS,
    modalKey: "mcp",
  },
  {
    id: EIntegrationProvider.TELEGRAM,
    nameKey: "list.telegram",
    descriptionKey: "list.telegramDescription",
    provider: EIntegrationProvider.TELEGRAM,
    modalKey: "telegram",
  },
  {
    id: EIntegrationProvider.BAMBOOHR,
    nameKey: "list.bamboohr",
    descriptionKey: "list.bamboohrDescription",
    provider: EIntegrationProvider.BAMBOOHR,
    modalKey: "bamboohr",
  },
  {
    id: EIntegrationProvider.NEXTCLOUD,
    nameKey: "list.nextcloud",
    descriptionKey: "list.nextcloudDescription",
    provider: EIntegrationProvider.NEXTCLOUD,
    modalKey: "nextcloud",
  },
  {
    id: EIntegrationProvider.DROPBOX,
    nameKey: "list.dropbox",
    descriptionKey: "list.dropboxDescription",
    provider: EIntegrationProvider.DROPBOX,
  },
  {
    id: EIntegrationProvider.TRELLO,
    nameKey: "list.trello",
    descriptionKey: "list.trelloDescription",
    provider: EIntegrationProvider.TRELLO,
  },
  {
    id: EIntegrationProvider.ZOOM,
    nameKey: "list.zoom",
    descriptionKey: "list.zoomDescription",
    provider: EIntegrationProvider.ZOOM,
  },
  {
    id: EIntegrationProvider.YANDEX,
    nameKey: "list.yandex",
    descriptionKey: "list.yandexDescription",
    provider: EIntegrationProvider.YANDEX,
  },
  {
    id: EIntegrationProvider.ONEPASSWORD,
    nameKey: "list.onePassword",
    descriptionKey: "list.onePasswordDescription",
    provider: EIntegrationProvider.ONEPASSWORD,
  },
  {
    id: EIntegrationProvider.TWENTYONE_RISK,
    nameKey: "list.twentyOneRisk",
    descriptionKey: "list.twentyOneRiskDescription",
    provider: EIntegrationProvider.TWENTYONE_RISK,
  },
  {
    id: EIntegrationProvider.TWOCHAT,
    nameKey: "list.twoChat",
    descriptionKey: "list.twoChatDescription",
    provider: EIntegrationProvider.TWOCHAT,
  },
  {
    id: EIntegrationProvider.ABLY,
    nameKey: "list.ably",
    descriptionKey: "list.ablyDescription",
    provider: EIntegrationProvider.ABLY,
  },
  {
    id: EIntegrationProvider.ABSTRACT,
    nameKey: "list.abstract",
    descriptionKey: "list.abstractDescription",
    provider: EIntegrationProvider.ABSTRACT,
  },
  {
    id: EIntegrationProvider.ABUSE_IPDB,
    nameKey: "list.abuseIpdb",
    descriptionKey: "list.abuseIpdbDescription",
    provider: EIntegrationProvider.ABUSE_IPDB,
  },
  {
    id: EIntegrationProvider.ABYSSALE,
    nameKey: "list.abyssale",
    descriptionKey: "list.abyssaleDescription",
    provider: EIntegrationProvider.ABYSSALE,
  },
  {
    id: EIntegrationProvider.ACCREDIBLE_CERTIFICATES,
    nameKey: "list.accredibleCertificates",
    descriptionKey: "list.accredibleCertificatesDescription",
    provider: EIntegrationProvider.ACCREDIBLE_CERTIFICATES,
  },
  {
    id: EIntegrationProvider.ACCULYNX,
    nameKey: "list.acculynx",
    descriptionKey: "list.acculynxDescription",
    provider: EIntegrationProvider.ACCULYNX,
  },
  {
    id: EIntegrationProvider.ACTIVE_CAMPAIGN,
    nameKey: "list.activeCampaign",
    descriptionKey: "list.activeCampaignDescription",
    provider: EIntegrationProvider.ACTIVE_CAMPAIGN,
  },
  {
    id: EIntegrationProvider.ACTIVETRAIL,
    nameKey: "list.activetrail",
    descriptionKey: "list.activetrailDescription",
    provider: EIntegrationProvider.ACTIVETRAIL,
  },
  {
    id: EIntegrationProvider.ADDRESSZEN,
    nameKey: "list.addresszen",
    descriptionKey: "list.addresszenDescription",
    provider: EIntegrationProvider.ADDRESSZEN,
  },
  {
    id: EIntegrationProvider.ADRAPID,
    nameKey: "list.adrapid",
    descriptionKey: "list.adrapidDescription",
    provider: EIntegrationProvider.ADRAPID,
  },
  {
    id: EIntegrationProvider.ADYNTEL,
    nameKey: "list.adyntel",
    descriptionKey: "list.adyntelDescription",
    provider: EIntegrationProvider.ADYNTEL,
  },
  {
    id: EIntegrationProvider.AEROLEADS,
    nameKey: "list.aeroleads",
    descriptionKey: "list.aeroleadsDescription",
    provider: EIntegrationProvider.AEROLEADS,
  },
  {
    id: EIntegrationProvider.AFFINDA,
    nameKey: "list.affinda",
    descriptionKey: "list.affindaDescription",
    provider: EIntegrationProvider.AFFINDA,
  },
  {
    id: EIntegrationProvider.AFFINITY,
    nameKey: "list.affinity",
    descriptionKey: "list.affinityDescription",
    provider: EIntegrationProvider.AFFINITY,
  },
  {
    id: EIntegrationProvider.AGENCYZOOM,
    nameKey: "list.agencyzoom",
    descriptionKey: "list.agencyzoomDescription",
    provider: EIntegrationProvider.AGENCYZOOM,
  },
  {
    id: EIntegrationProvider.AGENT_MAIL,
    nameKey: "list.agentMail",
    descriptionKey: "list.agentMailDescription",
    provider: EIntegrationProvider.AGENT_MAIL,
  },
  {
    id: EIntegrationProvider.AGENTQL,
    nameKey: "list.agentql",
    descriptionKey: "list.agentqlDescription",
    provider: EIntegrationProvider.AGENTQL,
  },
  {
    id: EIntegrationProvider.AGENTY,
    nameKey: "list.agenty",
    descriptionKey: "list.agentyDescription",
    provider: EIntegrationProvider.AGENTY,
  },
  {
    id: EIntegrationProvider.AGILED,
    nameKey: "list.agiled",
    descriptionKey: "list.agiledDescription",
    provider: EIntegrationProvider.AGILED,
  },
  {
    id: EIntegrationProvider.AGILITY_CMS,
    nameKey: "list.agilityCms",
    descriptionKey: "list.agilityCmsDescription",
    provider: EIntegrationProvider.AGILITY_CMS,
  },
  {
    id: EIntegrationProvider.AHREFS,
    nameKey: "list.ahrefs",
    descriptionKey: "list.ahrefsDescription",
    provider: EIntegrationProvider.AHREFS,
  },
  {
    id: EIntegrationProvider.AI_ML_API,
    nameKey: "list.aiMlApi",
    descriptionKey: "list.aiMlApiDescription",
    provider: EIntegrationProvider.AI_ML_API,
  },
  {
    id: EIntegrationProvider.AIVOOV,
    nameKey: "list.aivoov",
    descriptionKey: "list.aivoovDescription",
    provider: EIntegrationProvider.AIVOOV,
  },
  {
    id: EIntegrationProvider.ALCHEMY,
    nameKey: "list.alchemy",
    descriptionKey: "list.alchemyDescription",
    provider: EIntegrationProvider.ALCHEMY,
  },
  {
    id: EIntegrationProvider.ALGODOCS,
    nameKey: "list.algodocs",
    descriptionKey: "list.algodocsDescription",
    provider: EIntegrationProvider.ALGODOCS,
  },
  {
    id: EIntegrationProvider.ALGOLIA,
    nameKey: "list.algolia",
    descriptionKey: "list.algoliaDescription",
    provider: EIntegrationProvider.ALGOLIA,
  },
  {
    id: EIntegrationProvider.ALL_IMAGES_AI,
    nameKey: "list.allImagesAi",
    descriptionKey: "list.allImagesAiDescription",
    provider: EIntegrationProvider.ALL_IMAGES_AI,
  },
  {
    id: EIntegrationProvider.ALPHA_VANTAGE,
    nameKey: "list.alphaVantage",
    descriptionKey: "list.alphaVantageDescription",
    provider: EIntegrationProvider.ALPHA_VANTAGE,
  },
  {
    id: EIntegrationProvider.ALTOVIZ,
    nameKey: "list.altoviz",
    descriptionKey: "list.altovizDescription",
    provider: EIntegrationProvider.ALTOVIZ,
  },
  {
    id: EIntegrationProvider.ALTTEXT_AI,
    nameKey: "list.alttextAi",
    descriptionKey: "list.alttextAiDescription",
    provider: EIntegrationProvider.ALTTEXT_AI,
  },
  {
    id: EIntegrationProvider.AMARA,
    nameKey: "list.amara",
    descriptionKey: "list.amaraDescription",
    provider: EIntegrationProvider.AMARA,
  },
  {
    id: EIntegrationProvider.AMBEE,
    nameKey: "list.ambee",
    descriptionKey: "list.ambeeDescription",
    provider: EIntegrationProvider.AMBEE,
  },
  {
    id: EIntegrationProvider.AMBIENT_WEATHER,
    nameKey: "list.ambientWeather",
    descriptionKey: "list.ambientWeatherDescription",
    provider: EIntegrationProvider.AMBIENT_WEATHER,
  },
  {
    id: EIntegrationProvider.AMCARDS,
    nameKey: "list.amcards",
    descriptionKey: "list.amcardsDescription",
    provider: EIntegrationProvider.AMCARDS,
  },
  {
    id: EIntegrationProvider.AMPLITUDE,
    nameKey: "list.amplitude",
    descriptionKey: "list.amplitudeDescription",
    provider: EIntegrationProvider.AMPLITUDE,
  },
  {
    id: EIntegrationProvider.ANCHOR_BROWSER,
    nameKey: "list.anchorBrowser",
    descriptionKey: "list.anchorBrowserDescription",
    provider: EIntegrationProvider.ANCHOR_BROWSER,
  },
  {
    id: EIntegrationProvider.ANONYFLOW,
    nameKey: "list.anonyflow",
    descriptionKey: "list.anonyflowDescription",
    provider: EIntegrationProvider.ANONYFLOW,
  },
  {
    id: EIntegrationProvider.ANTHROPIC_ADMINISTRATOR,
    nameKey: "list.anthropicAdministrator",
    descriptionKey: "list.anthropicAdministratorDescription",
    provider: EIntegrationProvider.ANTHROPIC_ADMINISTRATOR,
  },
  {
    id: EIntegrationProvider.API_LABZ,
    nameKey: "list.apiLabz",
    descriptionKey: "list.apiLabzDescription",
    provider: EIntegrationProvider.API_LABZ,
  },
  {
    id: EIntegrationProvider.API_NINJAS,
    nameKey: "list.apiNinjas",
    descriptionKey: "list.apiNinjasDescription",
    provider: EIntegrationProvider.API_NINJAS,
  },
  {
    id: EIntegrationProvider.API_SPORTS,
    nameKey: "list.apiSports",
    descriptionKey: "list.apiSportsDescription",
    provider: EIntegrationProvider.API_SPORTS,
  },
  {
    id: EIntegrationProvider.API_BIBLE,
    nameKey: "list.apiBible",
    descriptionKey: "list.apiBibleDescription",
    provider: EIntegrationProvider.API_BIBLE,
  },
  {
    id: EIntegrationProvider.API2PDF,
    nameKey: "list.api2pdf",
    descriptionKey: "list.api2pdfDescription",
    provider: EIntegrationProvider.API2PDF,
  },
  {
    id: EIntegrationProvider.APIFLASH,
    nameKey: "list.apiflash",
    descriptionKey: "list.apiflashDescription",
    provider: EIntegrationProvider.APIFLASH,
  },
  {
    id: EIntegrationProvider.APIFY,
    nameKey: "list.apify",
    descriptionKey: "list.apifyDescription",
    provider: EIntegrationProvider.APIFY,
  },
  {
    id: EIntegrationProvider.APILIO,
    nameKey: "list.apilio",
    descriptionKey: "list.apilioDescription",
    provider: EIntegrationProvider.APILIO,
  },
  {
    id: EIntegrationProvider.APIPIE_AI,
    nameKey: "list.apipieAi",
    descriptionKey: "list.apipieAiDescription",
    provider: EIntegrationProvider.APIPIE_AI,
  },
  {
    id: EIntegrationProvider.APIVERVE,
    nameKey: "list.apiverve",
    descriptionKey: "list.apiverveDescription",
    provider: EIntegrationProvider.APIVERVE,
  },
  {
    id: EIntegrationProvider.APOLLO,
    nameKey: "list.apollo",
    descriptionKey: "list.apolloDescription",
    provider: EIntegrationProvider.APOLLO,
  },
  {
    id: EIntegrationProvider.APPCIRCLE,
    nameKey: "list.appcircle",
    descriptionKey: "list.appcircleDescription",
    provider: EIntegrationProvider.APPCIRCLE,
  },
  {
    id: EIntegrationProvider.APPDRAG,
    nameKey: "list.appdrag",
    descriptionKey: "list.appdragDescription",
    provider: EIntegrationProvider.APPDRAG,
  },
  {
    id: EIntegrationProvider.APPOINTO,
    nameKey: "list.appointo",
    descriptionKey: "list.appointoDescription",
    provider: EIntegrationProvider.APPOINTO,
  },
  {
    id: EIntegrationProvider.APPVEYOR,
    nameKey: "list.appveyor",
    descriptionKey: "list.appveyorDescription",
    provider: EIntegrationProvider.APPVEYOR,
  },
  {
    id: EIntegrationProvider.ARYN,
    nameKey: "list.aryn",
    descriptionKey: "list.arynDescription",
    provider: EIntegrationProvider.ARYN,
  },
  {
    id: EIntegrationProvider.ASCORA,
    nameKey: "list.ascora",
    descriptionKey: "list.ascoraDescription",
    provider: EIntegrationProvider.ASCORA,
  },
  {
    id: EIntegrationProvider.ASHBY,
    nameKey: "list.ashby",
    descriptionKey: "list.ashbyDescription",
    provider: EIntegrationProvider.ASHBY,
  },
  {
    id: EIntegrationProvider.ASIN_DATA_API,
    nameKey: "list.asinDataApi",
    descriptionKey: "list.asinDataApiDescription",
    provider: EIntegrationProvider.ASIN_DATA_API,
  },
  {
    id: EIntegrationProvider.ASTICA_AI,
    nameKey: "list.asticaAi",
    descriptionKey: "list.asticaAiDescription",
    provider: EIntegrationProvider.ASTICA_AI,
  },
  {
    id: EIntegrationProvider.ASYNC_INTERVIEW,
    nameKey: "list.asyncInterview",
    descriptionKey: "list.asyncInterviewDescription",
    provider: EIntegrationProvider.ASYNC_INTERVIEW,
  },
  {
    id: EIntegrationProvider.AUTOBOUND,
    nameKey: "list.autobound",
    descriptionKey: "list.autoboundDescription",
    provider: EIntegrationProvider.AUTOBOUND,
  },
  {
    id: EIntegrationProvider.AUTOM,
    nameKey: "list.autom",
    descriptionKey: "list.automDescription",
    provider: EIntegrationProvider.AUTOM,
  },
  {
    id: EIntegrationProvider.AYRSHARE,
    nameKey: "list.ayrshare",
    descriptionKey: "list.ayrshareDescription",
    provider: EIntegrationProvider.AYRSHARE,
  },
  {
    id: EIntegrationProvider.BANNERBEAR,
    nameKey: "list.bannerbear",
    descriptionKey: "list.bannerbearDescription",
    provider: EIntegrationProvider.BANNERBEAR,
  },
  {
    id: EIntegrationProvider.BART,
    nameKey: "list.bart",
    descriptionKey: "list.bartDescription",
    provider: EIntegrationProvider.BART,
  },
  {
    id: EIntegrationProvider.BASEROW,
    nameKey: "list.baserow",
    descriptionKey: "list.baserowDescription",
    provider: EIntegrationProvider.BASEROW,
  },
  {
    id: EIntegrationProvider.BASIN,
    nameKey: "list.basin",
    descriptionKey: "list.basinDescription",
    provider: EIntegrationProvider.BASIN,
  },
  {
    id: EIntegrationProvider.BEACONCHA_IN,
    nameKey: "list.beaconchaIn",
    descriptionKey: "list.beaconchaInDescription",
    provider: EIntegrationProvider.BEACONCHA_IN,
  },
  {
    id: EIntegrationProvider.BEACONSTAC,
    nameKey: "list.beaconstac",
    descriptionKey: "list.beaconstacDescription",
    provider: EIntegrationProvider.BEACONSTAC,
  },
  {
    id: EIntegrationProvider.BEEMINDER,
    nameKey: "list.beeminder",
    descriptionKey: "list.beeminderDescription",
    provider: EIntegrationProvider.BEEMINDER,
  },
  {
    id: EIntegrationProvider.BENCHMARK_EMAIL,
    nameKey: "list.benchmarkEmail",
    descriptionKey: "list.benchmarkEmailDescription",
    provider: EIntegrationProvider.BENCHMARK_EMAIL,
  },
  {
    id: EIntegrationProvider.BENZINGA,
    nameKey: "list.benzinga",
    descriptionKey: "list.benzingaDescription",
    provider: EIntegrationProvider.BENZINGA,
  },
  {
    id: EIntegrationProvider.BESTBUY,
    nameKey: "list.bestbuy",
    descriptionKey: "list.bestbuyDescription",
    provider: EIntegrationProvider.BESTBUY,
  },
  {
    id: EIntegrationProvider.BETTER_PROPOSALS,
    nameKey: "list.betterProposals",
    descriptionKey: "list.betterProposalsDescription",
    provider: EIntegrationProvider.BETTER_PROPOSALS,
  },
  {
    id: EIntegrationProvider.BETTER_STACK,
    nameKey: "list.betterStack",
    descriptionKey: "list.betterStackDescription",
    provider: EIntegrationProvider.BETTER_STACK,
  },
  {
    id: EIntegrationProvider.BETTERCONTACT,
    nameKey: "list.bettercontact",
    descriptionKey: "list.bettercontactDescription",
    provider: EIntegrationProvider.BETTERCONTACT,
  },
  {
    id: EIntegrationProvider.BIDSKETCH,
    nameKey: "list.bidsketch",
    descriptionKey: "list.bidsketchDescription",
    provider: EIntegrationProvider.BIDSKETCH,
  },
  {
    id: EIntegrationProvider.BIGDATACLOUD,
    nameKey: "list.bigdatacloud",
    descriptionKey: "list.bigdatacloudDescription",
    provider: EIntegrationProvider.BIGDATACLOUD,
  },
  {
    id: EIntegrationProvider.BIGMAILER,
    nameKey: "list.bigmailer",
    descriptionKey: "list.bigmailerDescription",
    provider: EIntegrationProvider.BIGMAILER,
  },
  {
    id: EIntegrationProvider.BIGML,
    nameKey: "list.bigml",
    descriptionKey: "list.bigmlDescription",
    provider: EIntegrationProvider.BIGML,
  },
  {
    id: EIntegrationProvider.BIGPICTURE_IO,
    nameKey: "list.bigpictureIo",
    descriptionKey: "list.bigpictureIoDescription",
    provider: EIntegrationProvider.BIGPICTURE_IO,
  },
  {
    id: EIntegrationProvider.BITQUERY,
    nameKey: "list.bitquery",
    descriptionKey: "list.bitqueryDescription",
    provider: EIntegrationProvider.BITQUERY,
  },
  {
    id: EIntegrationProvider.BLACKBAUD,
    nameKey: "list.blackbaud",
    descriptionKey: "list.blackbaudDescription",
    provider: EIntegrationProvider.BLACKBAUD,
  },
  {
    id: EIntegrationProvider.BLAZEMETER,
    nameKey: "list.blazemeter",
    descriptionKey: "list.blazemeterDescription",
    provider: EIntegrationProvider.BLAZEMETER,
  },
  {
    id: EIntegrationProvider.BLOCKNATIVE,
    nameKey: "list.blocknative",
    descriptionKey: "list.blocknativeDescription",
    provider: EIntegrationProvider.BLOCKNATIVE,
  },
  {
    id: EIntegrationProvider.BOLNA,
    nameKey: "list.bolna",
    descriptionKey: "list.bolnaDescription",
    provider: EIntegrationProvider.BOLNA,
  },
  {
    id: EIntegrationProvider.BOLOFORMS,
    nameKey: "list.boloforms",
    descriptionKey: "list.boloformsDescription",
    provider: EIntegrationProvider.BOLOFORMS,
  },
  {
    id: EIntegrationProvider.BOLT_IOT,
    nameKey: "list.boltIot",
    descriptionKey: "list.boltIotDescription",
    provider: EIntegrationProvider.BOLT_IOT,
  },
  {
    id: EIntegrationProvider.BONSAI,
    nameKey: "list.bonsai",
    descriptionKey: "list.bonsaiDescription",
    provider: EIntegrationProvider.BONSAI,
  },
  {
    id: EIntegrationProvider.BOOKINGMOOD,
    nameKey: "list.bookingmood",
    descriptionKey: "list.bookingmoodDescription",
    provider: EIntegrationProvider.BOOKINGMOOD,
  },
  {
    id: EIntegrationProvider.BOTPRESS,
    nameKey: "list.botpress",
    descriptionKey: "list.botpressDescription",
    provider: EIntegrationProvider.BOTPRESS,
  },
  {
    id: EIntegrationProvider.BOUNCER,
    nameKey: "list.bouncer",
    descriptionKey: "list.bouncerDescription",
    provider: EIntegrationProvider.BOUNCER,
  },
  {
    id: EIntegrationProvider.BOX,
    nameKey: "list.box",
    descriptionKey: "list.boxDescription",
    provider: EIntegrationProvider.BOX,
  },
  {
    id: EIntegrationProvider.BOXHERO,
    nameKey: "list.boxhero",
    descriptionKey: "list.boxheroDescription",
    provider: EIntegrationProvider.BOXHERO,
  },
  {
    id: EIntegrationProvider.BRANDFETCH,
    nameKey: "list.brandfetch",
    descriptionKey: "list.brandfetchDescription",
    provider: EIntegrationProvider.BRANDFETCH,
  },
  {
    id: EIntegrationProvider.BREATHE_HR,
    nameKey: "list.breatheHr",
    descriptionKey: "list.breatheHrDescription",
    provider: EIntegrationProvider.BREATHE_HR,
  },
  {
    id: EIntegrationProvider.BREEZE,
    nameKey: "list.breeze",
    descriptionKey: "list.breezeDescription",
    provider: EIntegrationProvider.BREEZE,
  },
  {
    id: EIntegrationProvider.BRIGHT_DATA,
    nameKey: "list.brightData",
    descriptionKey: "list.brightDataDescription",
    provider: EIntegrationProvider.BRIGHT_DATA,
  },
  {
    id: EIntegrationProvider.BRILLIANT_DIRECTORIES,
    nameKey: "list.brilliantDirectories",
    descriptionKey: "list.brilliantDirectoriesDescription",
    provider: EIntegrationProvider.BRILLIANT_DIRECTORIES,
  },
  {
    id: EIntegrationProvider.BROWSEAI,
    nameKey: "list.browseai",
    descriptionKey: "list.browseaiDescription",
    provider: EIntegrationProvider.BROWSEAI,
  },
  {
    id: EIntegrationProvider.APALEO,
    nameKey: "list.apaleo",
    descriptionKey: "list.apaleoDescription",
    provider: EIntegrationProvider.APALEO,
  },
  {
    id: EIntegrationProvider.APIFY_MCP,
    nameKey: "list.apifyMcp",
    descriptionKey: "list.apifyMcpDescription",
    provider: EIntegrationProvider.APIFY_MCP,
  },
  {
    id: EIntegrationProvider.ATTIO,
    nameKey: "list.attio",
    descriptionKey: "list.attioDescription",
    provider: EIntegrationProvider.ATTIO,
  },
  {
    id: EIntegrationProvider.BASECAMP,
    nameKey: "list.basecamp",
    descriptionKey: "list.basecampDescription",
    provider: EIntegrationProvider.BASECAMP,
  },
  {
    id: EIntegrationProvider.BITWARDEN,
    nameKey: "list.bitwarden",
    descriptionKey: "list.bitwardenDescription",
    provider: EIntegrationProvider.BITWARDEN,
  },
  {
    id: EIntegrationProvider.CAL,
    nameKey: "list.cal",
    descriptionKey: "list.calDescription",
    provider: EIntegrationProvider.CAL,
  },
  {
    id: EIntegrationProvider.CANVA,
    nameKey: "list.canva",
    descriptionKey: "list.canvaDescription",
    provider: EIntegrationProvider.CANVA,
  },
  {
    id: EIntegrationProvider.CANVAS,
    nameKey: "list.canvas",
    descriptionKey: "list.canvasDescription",
    provider: EIntegrationProvider.CANVAS,
  },
  {
    id: EIntegrationProvider.CAPSULE_CRM,
    nameKey: "list.capsuleCrm",
    descriptionKey: "list.capsuleCrmDescription",
    provider: EIntegrationProvider.CAPSULE_CRM,
  },
  {
    id: EIntegrationProvider.CLICKUP,
    nameKey: "list.clickup",
    descriptionKey: "list.clickupDescription",
    provider: EIntegrationProvider.CLICKUP,
  },
  {
    id: EIntegrationProvider.CLOCKIFY,
    nameKey: "list.clockify",
    descriptionKey: "list.clockifyDescription",
    provider: EIntegrationProvider.CLOCKIFY,
  },
  {
    id: EIntegrationProvider.CONTENTFUL,
    nameKey: "list.contentful",
    descriptionKey: "list.contentfulDescription",
    provider: EIntegrationProvider.CONTENTFUL,
  },
  {
    id: EIntegrationProvider.CROWDIN,
    nameKey: "list.crowdin",
    descriptionKey: "list.crowdinDescription",
    provider: EIntegrationProvider.CROWDIN,
  },
  {
    id: EIntegrationProvider.DART,
    nameKey: "list.dart",
    descriptionKey: "list.dartDescription",
    provider: EIntegrationProvider.DART,
  },
  {
    id: EIntegrationProvider.DATADOG,
    nameKey: "list.datadog",
    descriptionKey: "list.datadogDescription",
    provider: EIntegrationProvider.DATADOG,
  },
  {
    id: EIntegrationProvider.DIALPAD,
    nameKey: "list.dialpad",
    descriptionKey: "list.dialpadDescription",
    provider: EIntegrationProvider.DIALPAD,
  },
  {
    id: EIntegrationProvider.DIGITAL_OCEAN,
    nameKey: "list.digitalOcean",
    descriptionKey: "list.digitalOceanDescription",
    provider: EIntegrationProvider.DIGITAL_OCEAN,
  },
  {
    id: EIntegrationProvider.DISCORD_BOT,
    nameKey: "list.discordBot",
    descriptionKey: "list.discordBotDescription",
    provider: EIntegrationProvider.DISCORD_BOT,
  },
  {
    id: EIntegrationProvider.DOCUSIGN,
    nameKey: "list.docusign",
    descriptionKey: "list.docusignDescription",
    provider: EIntegrationProvider.DOCUSIGN,
  },
  {
    id: EIntegrationProvider.DROPBOX_SIGN,
    nameKey: "list.dropboxSign",
    descriptionKey: "list.dropboxSignDescription",
    provider: EIntegrationProvider.DROPBOX_SIGN,
  },
  {
    id: EIntegrationProvider.DUB,
    nameKey: "list.dub",
    descriptionKey: "list.dubDescription",
    provider: EIntegrationProvider.DUB,
  },
  {
    id: EIntegrationProvider.EPIC_GAMES,
    nameKey: "list.epicGames",
    descriptionKey: "list.epicGamesDescription",
    provider: EIntegrationProvider.EPIC_GAMES,
  },
  {
    id: EIntegrationProvider.EVENTBRITE,
    nameKey: "list.eventbrite",
    descriptionKey: "list.eventbriteDescription",
    provider: EIntegrationProvider.EVENTBRITE,
  },
  {
    id: EIntegrationProvider.EXIST,
    nameKey: "list.exist",
    descriptionKey: "list.existDescription",
    provider: EIntegrationProvider.EXIST,
  },
  {
    id: EIntegrationProvider.FACEBOOK,
    nameKey: "list.facebook",
    descriptionKey: "list.facebookDescription",
    provider: EIntegrationProvider.FACEBOOK,
  },
  {
    id: EIntegrationProvider.FATHOM,
    nameKey: "list.fathom",
    descriptionKey: "list.fathomDescription",
    provider: EIntegrationProvider.FATHOM,
  },
  {
    id: EIntegrationProvider.FIGMA,
    nameKey: "list.figma",
    descriptionKey: "list.figmaDescription",
    provider: EIntegrationProvider.FIGMA,
  },
  {
    id: EIntegrationProvider.FLY,
    nameKey: "list.fly",
    descriptionKey: "list.flyDescription",
    provider: EIntegrationProvider.FLY,
  },
  {
    id: EIntegrationProvider.FOLLOW_UP_BOSS,
    nameKey: "list.followUpBoss",
    descriptionKey: "list.followUpBossDescription",
    provider: EIntegrationProvider.FOLLOW_UP_BOSS,
  },
  {
    id: EIntegrationProvider.FREEAGENT,
    nameKey: "list.freeagent",
    descriptionKey: "list.freeagentDescription",
    provider: EIntegrationProvider.FREEAGENT,
  },
  {
    id: EIntegrationProvider.FRESHBOOKS,
    nameKey: "list.freshbooks",
    descriptionKey: "list.freshbooksDescription",
    provider: EIntegrationProvider.FRESHBOOKS,
  },
  {
    id: EIntegrationProvider.GONG,
    nameKey: "list.gong",
    descriptionKey: "list.gongDescription",
    provider: EIntegrationProvider.GONG,
  },
  {
    id: EIntegrationProvider.GOOGLE_ADMIN,
    nameKey: "list.googleAdmin",
    descriptionKey: "list.googleAdminDescription",
    provider: EIntegrationProvider.GOOGLE_ADMIN,
  },
  {
    id: EIntegrationProvider.GOOGLE_ADS,
    nameKey: "list.googleAds",
    descriptionKey: "list.googleAdsDescription",
    provider: EIntegrationProvider.GOOGLE_ADS,
  },
  {
    id: EIntegrationProvider.GOOGLE_ANALYTICS,
    nameKey: "list.googleAnalytics",
    descriptionKey: "list.googleAnalyticsDescription",
    provider: EIntegrationProvider.GOOGLE_ANALYTICS,
  },
  {
    id: EIntegrationProvider.GOOGLE_BIGQUERY,
    nameKey: "list.googleBigQuery",
    descriptionKey: "list.googleBigQueryDescription",
    provider: EIntegrationProvider.GOOGLE_BIGQUERY,
  },
  {
    id: EIntegrationProvider.GOOGLE_CLASSROOM,
    nameKey: "list.googleClassroom",
    descriptionKey: "list.googleClassroomDescription",
    provider: EIntegrationProvider.GOOGLE_CLASSROOM,
  },
  {
    id: EIntegrationProvider.GOOGLE_DOCS,
    nameKey: "list.googleDocsComposio",
    descriptionKey: "list.googleDocsComposioDescription",
    provider: EIntegrationProvider.GOOGLE_DOCS,
  },
  {
    id: EIntegrationProvider.GOOGLE_PHOTOS,
    nameKey: "list.googlePhotos",
    descriptionKey: "list.googlePhotosDescription",
    provider: EIntegrationProvider.GOOGLE_PHOTOS,
  },
  {
    id: EIntegrationProvider.GOOGLE_SEARCH_CONSOLE,
    nameKey: "list.googleSearchConsole",
    descriptionKey: "list.googleSearchConsoleDescription",
    provider: EIntegrationProvider.GOOGLE_SEARCH_CONSOLE,
  },
  {
    id: EIntegrationProvider.GOOGLE_SLIDES,
    nameKey: "list.googleSlides",
    descriptionKey: "list.googleSlidesDescription",
    provider: EIntegrationProvider.GOOGLE_SLIDES,
  },
  {
    id: EIntegrationProvider.GOOGLE_SUPER,
    nameKey: "list.googleSuper",
    descriptionKey: "list.googleSuperDescription",
    provider: EIntegrationProvider.GOOGLE_SUPER,
  },
  {
    id: EIntegrationProvider.GOOGLE_TASKS,
    nameKey: "list.googleTasks",
    descriptionKey: "list.googleTasksDescription",
    provider: EIntegrationProvider.GOOGLE_TASKS,
  },
  {
    id: EIntegrationProvider.GUMROAD,
    nameKey: "list.gumroad",
    descriptionKey: "list.gumroadDescription",
    provider: EIntegrationProvider.GUMROAD,
  },
  {
    id: EIntegrationProvider.HARVEST,
    nameKey: "list.harvest",
    descriptionKey: "list.harvestDescription",
    provider: EIntegrationProvider.HARVEST,
  },
  {
    id: EIntegrationProvider.HELP_SCOUT,
    nameKey: "list.helpScout",
    descriptionKey: "list.helpScoutDescription",
    provider: EIntegrationProvider.HELP_SCOUT,
  },
  {
    id: EIntegrationProvider.HUGGING_FACE,
    nameKey: "list.huggingFace",
    descriptionKey: "list.huggingFaceDescription",
    provider: EIntegrationProvider.HUGGING_FACE,
  },
  {
    id: EIntegrationProvider.INSIGHTO_AI,
    nameKey: "list.insightoAi",
    descriptionKey: "list.insightoAiDescription",
    provider: EIntegrationProvider.INSIGHTO_AI,
  },
  {
    id: EIntegrationProvider.INSTAGRAM,
    nameKey: "list.instagram",
    descriptionKey: "list.instagramDescription",
    provider: EIntegrationProvider.INSTAGRAM,
  },
  {
    id: EIntegrationProvider.INTERCOM,
    nameKey: "list.intercom",
    descriptionKey: "list.intercomDescription",
    provider: EIntegrationProvider.INTERCOM,
  },
  {
    id: EIntegrationProvider.KIT,
    nameKey: "list.kit",
    descriptionKey: "list.kitDescription",
    provider: EIntegrationProvider.KIT,
  },
  {
    id: EIntegrationProvider.KLAVIYO,
    nameKey: "list.klaviyo",
    descriptionKey: "list.klaviyoDescription",
    provider: EIntegrationProvider.KLAVIYO,
  },
  {
    id: EIntegrationProvider.LEVER,
    nameKey: "list.lever",
    descriptionKey: "list.leverDescription",
    provider: EIntegrationProvider.LEVER,
  },
  {
    id: EIntegrationProvider.LINKEDIN,
    nameKey: "list.linkedin",
    descriptionKey: "list.linkedinDescription",
    provider: EIntegrationProvider.LINKEDIN,
  },
  {
    id: EIntegrationProvider.LINKEDIN_ADS,
    nameKey: "list.linkedinAds",
    descriptionKey: "list.linkedinAdsDescription",
    provider: EIntegrationProvider.LINKEDIN_ADS,
  },
  {
    id: EIntegrationProvider.LINKHUT,
    nameKey: "list.linkhut",
    descriptionKey: "list.linkhutDescription",
    provider: EIntegrationProvider.LINKHUT,
  },
  {
    id: EIntegrationProvider.LODGIFY,
    nameKey: "list.lodgify",
    descriptionKey: "list.lodgifyDescription",
    provider: EIntegrationProvider.LODGIFY,
  },
  {
    id: EIntegrationProvider.MAILCHIMP,
    nameKey: "list.mailchimp",
    descriptionKey: "list.mailchimpDescription",
    provider: EIntegrationProvider.MAILCHIMP,
  },
  {
    id: EIntegrationProvider.MATTERPORT,
    nameKey: "list.matterport",
    descriptionKey: "list.matterportDescription",
    provider: EIntegrationProvider.MATTERPORT,
  },
  {
    id: EIntegrationProvider.META_ADS,
    nameKey: "list.metaAds",
    descriptionKey: "list.metaAdsDescription",
    provider: EIntegrationProvider.META_ADS,
  },
  {
    id: EIntegrationProvider.MIRO,
    nameKey: "list.miro",
    descriptionKey: "list.miroDescription",
    provider: EIntegrationProvider.MIRO,
  },
  {
    id: EIntegrationProvider.MONDAY,
    nameKey: "list.monday",
    descriptionKey: "list.mondayDescription",
    provider: EIntegrationProvider.MONDAY,
  },
  {
    id: EIntegrationProvider.MONDAY_MCP,
    nameKey: "list.mondayMcp",
    descriptionKey: "list.mondayMcpDescription",
    provider: EIntegrationProvider.MONDAY_MCP,
  },
  {
    id: EIntegrationProvider.MONEYBIRD,
    nameKey: "list.moneybird",
    descriptionKey: "list.moneybirdDescription",
    provider: EIntegrationProvider.MONEYBIRD,
  },
  {
    id: EIntegrationProvider.MURAL,
    nameKey: "list.mural",
    descriptionKey: "list.muralDescription",
    provider: EIntegrationProvider.MURAL,
  },
  {
    id: EIntegrationProvider.ODOO,
    nameKey: "list.odoo",
    descriptionKey: "list.odooDescription",
    provider: EIntegrationProvider.ODOO,
  },
  {
    id: EIntegrationProvider.OMNISEND,
    nameKey: "list.omnisend",
    descriptionKey: "list.omnisendDescription",
    provider: EIntegrationProvider.OMNISEND,
  },
  {
    id: EIntegrationProvider.PAGERDUTY,
    nameKey: "list.pagerduty",
    descriptionKey: "list.pagerdutyDescription",
    provider: EIntegrationProvider.PAGERDUTY,
  },
  {
    id: EIntegrationProvider.PARMA,
    nameKey: "list.parma",
    descriptionKey: "list.parmaDescription",
    provider: EIntegrationProvider.PARMA,
  },
  {
    id: EIntegrationProvider.PINECONE,
    nameKey: "list.pinecone",
    descriptionKey: "list.pineconeDescription",
    provider: EIntegrationProvider.PINECONE,
  },
  {
    id: EIntegrationProvider.PIPEDRIVE,
    nameKey: "list.pipedrive",
    descriptionKey: "list.pipedriveDescription",
    provider: EIntegrationProvider.PIPEDRIVE,
  },
  {
    id: EIntegrationProvider.PRISMA,
    nameKey: "list.prisma",
    descriptionKey: "list.prismaDescription",
    provider: EIntegrationProvider.PRISMA,
  },
  {
    id: EIntegrationProvider.PRODUCTBOARD,
    nameKey: "list.productboard",
    descriptionKey: "list.productboardDescription",
    provider: EIntegrationProvider.PRODUCTBOARD,
  },
  {
    id: EIntegrationProvider.PUSHBULLET,
    nameKey: "list.pushbullet",
    descriptionKey: "list.pushbulletDescription",
    provider: EIntegrationProvider.PUSHBULLET,
  },
  {
    id: EIntegrationProvider.QUICKBOOKS,
    nameKey: "list.quickbooks",
    descriptionKey: "list.quickbooksDescription",
    provider: EIntegrationProvider.QUICKBOOKS,
  },
  {
    id: EIntegrationProvider.RAMP,
    nameKey: "list.ramp",
    descriptionKey: "list.rampDescription",
    provider: EIntegrationProvider.RAMP,
  },
  {
    id: EIntegrationProvider.REDDIT,
    nameKey: "list.reddit",
    descriptionKey: "list.redditDescription",
    provider: EIntegrationProvider.REDDIT,
  },
  {
    id: EIntegrationProvider.REDDIT_ADS,
    nameKey: "list.redditAds",
    descriptionKey: "list.redditAdsDescription",
    provider: EIntegrationProvider.REDDIT_ADS,
  },
  {
    id: EIntegrationProvider.ROAM,
    nameKey: "list.roam",
    descriptionKey: "list.roamDescription",
    provider: EIntegrationProvider.ROAM,
  },
  {
    id: EIntegrationProvider.BOLDSIGN,
    nameKey: "list.boldsign",
    descriptionKey: "list.boldsignDescription",
    provider: EIntegrationProvider.BOLDSIGN,
  },
  {
    id: EIntegrationProvider.BORNEO,
    nameKey: "list.borneo",
    descriptionKey: "list.borneoDescription",
    provider: EIntegrationProvider.BORNEO,
  },
  {
    id: EIntegrationProvider.BREVO,
    nameKey: "list.brevo",
    descriptionKey: "list.brevoDescription",
    provider: EIntegrationProvider.BREVO,
  },
  {
    id: EIntegrationProvider.BREX,
    nameKey: "list.brex",
    descriptionKey: "list.brexDescription",
    provider: EIntegrationProvider.BREX,
  },
  {
    id: EIntegrationProvider.BITBUCKET,
    nameKey: "list.bitbucket",
    descriptionKey: "list.bitbucketDescription",
    provider: EIntegrationProvider.BITBUCKET,
  },
  {
    id: EIntegrationProvider.DISCORD,
    nameKey: "list.discord",
    descriptionKey: "list.discordDescription",
    provider: EIntegrationProvider.DISCORD,
  },
  {
    id: EIntegrationProvider.SUPABASE,
    nameKey: "list.supabase",
    descriptionKey: "list.supabaseDescription",
    provider: EIntegrationProvider.SUPABASE,
  },
  {
    id: EIntegrationProvider.YOUTUBE,
    nameKey: "list.youtube",
    descriptionKey: "list.youtubeDescription",
    provider: EIntegrationProvider.YOUTUBE,
  },
  {
    id: EIntegrationProvider.WEBEX,
    nameKey: "list.webex",
    descriptionKey: "list.webexDescription",
    provider: EIntegrationProvider.WEBEX,
  },
  {
    id: EIntegrationProvider.WAKATIME,
    nameKey: "list.wakatime",
    descriptionKey: "list.wakatimeDescription",
    provider: EIntegrationProvider.WAKATIME,
  },
  {
    id: EIntegrationProvider.TYPEFORM,
    nameKey: "list.typeform",
    descriptionKey: "list.typeformDescription",
    provider: EIntegrationProvider.TYPEFORM,
  },
  {
    id: EIntegrationProvider.TIMELY,
    nameKey: "list.timely",
    descriptionKey: "list.timelyDescription",
    provider: EIntegrationProvider.TIMELY,
  },
  {
    id: EIntegrationProvider.TICKTICK,
    nameKey: "list.ticktick",
    descriptionKey: "list.ticktickDescription",
    provider: EIntegrationProvider.TICKTICK,
  },
  {
    id: EIntegrationProvider.STRAVA,
    nameKey: "list.strava",
    descriptionKey: "list.stravaDescription",
    provider: EIntegrationProvider.STRAVA,
  },
  {
    id: EIntegrationProvider.STACK_EXCHANGE,
    nameKey: "list.stackExchange",
    descriptionKey: "list.stackExchangeDescription",
    provider: EIntegrationProvider.STACK_EXCHANGE,
  },
  {
    id: EIntegrationProvider.SQUARE,
    nameKey: "list.square",
    descriptionKey: "list.squareDescription",
    provider: EIntegrationProvider.SQUARE,
  },
  {
    id: EIntegrationProvider.SPLITWISE,
    nameKey: "list.splitwise",
    descriptionKey: "list.splitwiseDescription",
    provider: EIntegrationProvider.SPLITWISE,
  },
  {
    id: EIntegrationProvider.SENTRY,
    nameKey: "list.sentry",
    descriptionKey: "list.sentryDescription",
    provider: EIntegrationProvider.SENTRY,
  },
  {
    id: EIntegrationProvider.ONENOTE,
    nameKey: "list.onenote",
    descriptionKey: "list.onenoteDescription",
    provider: EIntegrationProvider.ONENOTE,
  },
  {
    id: EIntegrationProvider.WORKABLE,
    nameKey: "list.workable",
    descriptionKey: "list.workableDescription",
    provider: EIntegrationProvider.WORKABLE,
  },
  {
    id: EIntegrationProvider.MICROSOFT_PLANNER,
    nameKey: "list.microsoftPlanner",
    descriptionKey: "list.microsoftPlannerDescription",
    provider: EIntegrationProvider.MICROSOFT_PLANNER,
  },
  {
    id: EIntegrationProvider.MICROSOFT_TODO,
    nameKey: "list.microsoftTodo",
    descriptionKey: "list.microsoftTodoDescription",
    provider: EIntegrationProvider.MICROSOFT_TODO,
  },
  {
    id: EIntegrationProvider.MICROSOFT_BOOKINGS,
    nameKey: "list.microsoftBookings",
    descriptionKey: "list.microsoftBookingsDescription",
    provider: EIntegrationProvider.MICROSOFT_BOOKINGS,
  },
];

/**
 * NOTE: NSAI-5354 — RFC 7230 `token`, the grammar an HTTP header name must
 * satisfy. Enforced wherever a custom auth header is named (EMcpAuthMode.HEADER)
 * because undici throws on an invalid name when the request is built, which the
 * member would see as an opaque connection failure rather than a form error.
 *
 * Mirrors HTTP_HEADER_NAME_REGEX in the server's
 * src/integration/mcps/mcps.constants.ts — the same value is enforced on the DTO.
 */
export const HTTP_HEADER_NAME_REGEX = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

/**
 * NOTE: NSAI-5354 — MCP transport choices for the connect and org MCP forms.
 *
 * AUTO leads because it is the right answer for almost every server: the backend
 * probes Streamable HTTP then SSE and stores whichever answered, so a member
 * never has to know which one their server speaks. The explicit options exist for
 * a server that misbehaves under probing.
 */
export const MCP_TRANSPORT_OPTIONS = [
  {
    value: EMcpTransport.AUTO,
    labelKey: "mcp.transportAuto",
    descriptionKey: "mcp.transportAutoDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpTransportAuto",
  },
  {
    value: EMcpTransport.HTTP,
    labelKey: "mcp.transportHttp",
    descriptionKey: "mcp.transportHttpDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpTransportHttp",
  },
  {
    value: EMcpTransport.SSE,
    labelKey: "mcp.transportSse",
    descriptionKey: "mcp.transportSseDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpTransportSse",
  },
] as const;

/**
 * NOTE: NSAI-5354 — MCP auth choices. BEARER leads because it is what the
 * platform did unconditionally before this change and what most credentialed MCP
 * servers expect, so the default preserves existing behaviour.
 */
export const MCP_AUTH_MODE_OPTIONS = [
  {
    value: EMcpAuthMode.BEARER,
    labelKey: "mcp.authBearer",
    descriptionKey: "mcp.authBearerDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpAuthBearer",
  },
  {
    value: EMcpAuthMode.NONE,
    labelKey: "mcp.authNone",
    descriptionKey: "mcp.authNoneDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpAuthNone",
  },
  {
    value: EMcpAuthMode.HEADER,
    labelKey: "mcp.authHeader",
    descriptionKey: "mcp.authHeaderDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpAuthHeader",
  },
  {
    value: EMcpAuthMode.RAW,
    labelKey: "mcp.authRaw",
    descriptionKey: "mcp.authRawDescription",
    orgLabelKey: "organizationIntegrationConfig.form.mcpAuthRaw",
  },
] as const;

// NOTE: The auth modes that require the member to supply a credential — used to
// show/hide the token field. NONE has nothing to supply.
//
// Typed as the full enum rather than `as const`: a narrow three-member tuple made
// `.includes(someAuthMode)` a type error, which is why callers reached for
// `.some(mode => mode === x)` — the same check written the long way round. Mirrors
// the server's own copy in mcps.constants.ts.
export const MCP_STATIC_CREDENTIAL_AUTH_MODES: readonly EMcpAuthMode[] = [
  EMcpAuthMode.BEARER,
  EMcpAuthMode.HEADER,
  EMcpAuthMode.RAW,
];

// NOTE: The providers that have a section on the organization Integration
// screen — read both by the screen (which section to render) and by the
// settings shell (whether to offer the tab at all). Held together because they
// answer the same question and drifted once already: MCP rendered its section
// unconditionally while its siblings were each gated, so an owner on a
// deployment without `mcps` could configure servers no member could connect to.
//
// TWO lists, because the two clients genuinely render different section sets:
// mobile has no Kontur Diadoc or Nextcloud section. Gating the mobile tab on
// the web list would offer a tab that renders nothing but its heading on a
// deployment that enables only one of those — the very bug this is closing. Add
// a provider here when its section ships on that client, not before.
//
// Matched on the EIntegrationProvider value exactly, against the enabled list
// the server returns. The server keeps its own comparison
// (assertDisabledProvidersUnchanged) because hiding a form is not a guard.
export const WEB_ORGANIZATION_CONFIGURABLE_PROVIDERS: readonly EIntegrationProvider[] =
  [
    EIntegrationProvider.BITRIX24,
    EIntegrationProvider.AZURE_MES,
    EIntegrationProvider.KONTUR_FOCUS,
    EIntegrationProvider.KONTUR_DIADOC,
    EIntegrationProvider.ATLASSIAN_JIRA_DC,
    EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
    EIntegrationProvider.NEXTCLOUD,
    EIntegrationProvider.MCPS,
  ];

export const MOBILE_ORGANIZATION_CONFIGURABLE_PROVIDERS: readonly EIntegrationProvider[] =
  [
    EIntegrationProvider.BITRIX24,
    EIntegrationProvider.AZURE_MES,
    EIntegrationProvider.KONTUR_FOCUS,
    EIntegrationProvider.ATLASSIAN_JIRA_DC,
    EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
    EIntegrationProvider.MCPS,
  ];
